/*
// Licensed Materials - Property of HCL
// (C) Copyright HCL Technologies Limited 2001, 2021
// All Rights Reserved
*/

import * as constants from '../constants/Constants'

/**
 * Validates that a URL is a non-empty string
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if valid, false otherwise
 */
function isValidUrlString(url) {
  if (!url || typeof url !== 'string') {
    console.error('Invalid URL: URL must be a non-empty string');
    return false;
  }
  
  // Add maximum URL length check (2083 is IE's limit, a safe default)
  if (url.length > 2083) {
    console.error('Invalid URL: URL exceeds maximum length');
    return false;
  }
  
  return true;
}

/**
 * Checks if a URL contains dangerous protocols
 * @param {string} url - The URL to check
 * @returns {boolean} - True if dangerous protocol detected, false otherwise
 */
function hasDangerousProtocol(url) {
  const lowerUrl = url.toLowerCase();
  for (const protocol of constants.DANGEROUS_PROTOCOLS) {
    if (lowerUrl.startsWith(protocol)) {
      console.error(`Dangerous protocol detected: ${protocol}`);
      return true;
    }
  }
  return false;
}

/**
 * Checks if a URL contains XSS patterns
 * @param {string} url - The URL to check
 * @returns {boolean} - True if XSS pattern detected, false otherwise
 */
function hasXSSPatterns(url) {
  for (const pattern of constants.XSS_PATTERNS) {
    if (pattern.test(url)) {
      console.error('Potential XSS pattern detected in URL');
      return true;
    }
  }
  return false;
}

/**
 * Checks for URL encoding attacks including double-encoding
 * @param {string} url - The URL to check
 * @returns {boolean} - True if encoding attack detected, false otherwise
 */
function hasEncodingAttack(url) {
  try {
    const decodedUrl = decodeURIComponent(url);
    const doubleDecoded = decodeURIComponent(decodedUrl);
    
    if (doubleDecoded !== decodedUrl) {
      // Check if double-decoded URL contains dangerous patterns
      for (const protocol of constants.DANGEROUS_PROTOCOLS) {
        if (doubleDecoded.toLowerCase().startsWith(protocol)) {
          console.error('Double-encoded dangerous protocol detected');
          return true;
        }
      }
    }
    return false;
  } catch (e) {
    console.error('Invalid URL encoding:', e);
    return true;
  }
}

/**
 * Validates the protocol of a parsed URL
 * @param {URL} parsedUrl - The parsed URL object
 * @returns {boolean} - True if protocol is valid, false otherwise
 */
function hasValidProtocol(parsedUrl) {
  if (!constants.ALLOWED_PROTOCOLS.includes(parsedUrl.protocol)) {
    console.error(`Invalid protocol: ${parsedUrl.protocol}. Only http and https are allowed`);
    return false;
  }
  return true;
}

/**
 * Checks for homograph attacks using control characters
 * @param {URL} parsedUrl - The parsed URL object
 * @returns {boolean} - True if homograph attack detected, false otherwise
 */
function hasHomographAttack(parsedUrl) {
  if (/[\u0000-\u001F\u007F-\u009F]/.test(parsedUrl.hostname)) {
    console.error('Control characters detected in hostname');
    return true;
  }
  return false;
}

/**
 * Validates domain against allowed list
 * @param {URL} parsedUrl - The parsed URL object
 * @param {string[]} allowedDomains - List of allowed domains
 * @returns {boolean} - True if domain is allowed, false otherwise
 */
function isDomainAllowed(parsedUrl, allowedDomains) {
  if (allowedDomains.length === 0) {
    return true; // No restrictions if no allowed domains specified
  }
  
  const hostname = parsedUrl.hostname.toLowerCase();
  
  const isAllowed = allowedDomains.some(domain => {
    const lowerDomain = domain.toLowerCase();
    // Exact match or subdomain match
    // Ensure proper subdomain validation: hostname must be exactly domain or end with .domain
    if (hostname === lowerDomain) {
      return true;
    }
    if (hostname.endsWith('.' + lowerDomain)) {
      // Additional safety: ensure it's a proper subdomain, not a similar domain
      // e.g., "google.com.evil.com" will NOT match "google.com"
      const parts = hostname.split('.');
      const domainParts = lowerDomain.split('.');
      // Check that the domain parts match from the end
      return parts.slice(-domainParts.length).join('.') === lowerDomain;
    }
    return false;
  });
  
  if (!isAllowed) {
    console.error(`Domain ${parsedUrl.hostname} is not in the allowed list`);
    return false;
  }
  return true;
}

/**
 * Checks for embedded credentials in URL
 * @param {URL} parsedUrl - The parsed URL object
 * @returns {boolean} - True if credentials detected, false otherwise
 */
function hasEmbeddedCredentials(parsedUrl) {
  if (parsedUrl.username || parsedUrl.password) {
    console.error('URLs with embedded credentials are not allowed');
    return true;
  }
  return false;
}

/**
 * Checks for suspicious ports that could be used in attacks
 * @param {URL} parsedUrl - The parsed URL object
 * @returns {boolean} - True if suspicious port detected, false otherwise
 */
function hasSuspiciousPort(parsedUrl) {
  // Suspicious ports that could be used for attacks
  const suspiciousPorts = [
    '22',   // SSH
    '23',   // Telnet
    '25',   // SMTP
    '3389', // RDP
    '5900', // VNC
  ];
  
  if (parsedUrl.port && suspiciousPorts.includes(parsedUrl.port)) {
    console.error(`Suspicious port detected: ${parsedUrl.port}`);
    return true;
  }
  return false;
}

/**
 * Checks for CRLF injection that could be used for HTTP response splitting
 * @param {string} url - The URL to check
 * @returns {boolean} - True if CRLF detected, false otherwise
 */
function hasCRLFInjection(url) {
  // Check for CRLF characters that could be used for HTTP response splitting
  if (/[\r\n]|%0[dD]|%0[aA]/.test(url)) {
    console.error('CRLF injection detected in URL');
    return true;
  }
  return false;
}

/**
 * Checks for suspicious URL patterns that could indicate attacks
 * @param {string} url - The URL to check
 * @returns {boolean} - True if suspicious pattern detected, false otherwise
 */
function hasSuspiciousPatterns(url) {
  for (const pattern of constants.SUSPICIOUS_URL_PATTERNS) {
    if (pattern.test(url)) {
      console.error('Suspicious pattern detected in URL');
      return true;
    }
  }
  return false;
}

/**
 * Validates and sanitizes a URL to prevent open redirect vulnerabilities
 * @param {string} url - The URL to validate
 * @param {Object} options - Optional validation options
 * @param {string[]} options.allowedDomains - List of allowed domains (default: empty array)
 * @returns {string|null} - The sanitized URL if valid, null otherwise
 */
export function validateAndSanitizeURL(url, options = {}) {
  // Step 1: Validate URL is a string
  if (!isValidUrlString(url)) {
    return null;
  }

  const trimmedUrl = url.trim();
  
  if (hasDangerousProtocol(trimmedUrl)) {
    return null;
  }

  if (hasXSSPatterns(trimmedUrl)) {
    return null;
  }
  
  if (hasCRLFInjection(trimmedUrl)) {
    return null;
  }

  if (hasEncodingAttack(trimmedUrl)) {
    return null;
  }

  try {
    const parsedUrl = new URL(trimmedUrl, window.location.origin);
    
    if (!hasValidProtocol(parsedUrl)) {
      return null;
    }

    if (hasHomographAttack(parsedUrl)) {
      return null;
    }

    const allowedDomains = options.allowedDomains || [];
    if (!isDomainAllowed(parsedUrl, allowedDomains)) {
      return null;
    }

    if (hasEmbeddedCredentials(parsedUrl)) {
      return null;
    }
    
    if (hasSuspiciousPort(parsedUrl)) {
      return null;
    }

    if (hasSuspiciousPatterns(trimmedUrl)) {
      return null;
    }

    return parsedUrl.href;
    
  } catch (error) {
    console.error('Invalid URL format:', error);
    return null;
  }
}